// src/app/features/trade/TradeTicket.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createTradeRequest } from "./tradeApi.js";
import { DELIVERY_OPTIONS, getOptionBySeconds, toPayoutMultiplier } from "./durationProfit.js";
import { ChevronUp, ChevronDown, DollarSign, Info, AlertCircle, Shield } from "lucide-react";

// NEW: wallet overview
import { getWalletOverview } from "../wallet/walletApi.js";

const PLATFORM_FEE_RATE = 0.005; // 0.5%

// Asset to network mapping
const ASSET_NETWORK_MAP = {
  USDT: "TRON",
  BTC: "MAINNET",
  ETH: "MAINNET",
};

// Network display names
const NETWORK_DISPLAY = {
  MAINNET: "Ethereum Mainnet",
  TRON: "Tron (TRC20)",
};

function sanitizeNumberInput(value) {
  const s = String(value ?? "")
    .replace(/,/g, "")
    .replace(/\s/g, "")
    .replace(/[^0-9.]/g, "");
  const parts = s.split(".");
  if (parts.length <= 2) return s;
  return `${parts[0]}.${parts.slice(1).join("")}`;
}

function formatAmount(n, decimals = 2) {
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
}

function CurrencyIcon({ code }) {
  const common = "h-4 w-4";
  if (code === "BTC") {
    return (
      <span className={`${common} inline-flex items-center justify-center rounded-full bg-orange-500/20 text-orange-300 font-bold`}>
        ₿
      </span>
    );
  }
  if (code === "USDT") {
    return (
      <span className={`${common} inline-flex items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300 font-bold`}>
        $
      </span>
    );
  }
  if (code === "ETH") {
    return (
      <span className={`${common} inline-flex items-center justify-center rounded-full bg-indigo-500/20 text-indigo-300 font-bold`}>
        Ξ
      </span>
    );
  }
  return (
    <span className={`${common} inline-flex items-center justify-center rounded-full bg-gray-700 text-gray-200 font-bold`}>
      ?
    </span>
  );
}

function Pill({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 rounded-lg border text-sm font-semibold transition ${
        active
          ? "bg-primary-600/20 border-primary-500/40 text-white"
          : "bg-gray-900/40 border-gray-800 text-gray-300 hover:border-gray-700"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Props:
 * - selectedMarket (string)
 * - assetClass (string)
 * - leverage (number) optional: default 1. If >1 user can trade up to available*leverage.
 * - defaultNetwork (string) optional: default "MAINNET"
 */
export default function TradeTicket({
  selectedMarket = "BTCUSDT",
  assetClass,
  leverage = 1,
  defaultNetwork = "MAINNET",
}) {
  const qc = useQueryClient();

  const [market, setMarket] = useState(selectedMarket);
  const [direction, setDirection] = useState("UP"); // "UP" | "DOWN"
  const [accountMode, setAccountMode] = useState("REAL"); // "REAL" | "DEMO"

  const [amountInput, setAmountInput] = useState("");
  const [showMaxWarning, setShowMaxWarning] = useState(false);

  const [deliverySeconds, setDeliverySeconds] = useState(DELIVERY_OPTIONS[0].seconds);

  const [currency, setCurrency] = useState("USDT"); // BTC | USDT | ETH
  const supportedCurrencies = ["USDT", "BTC", "ETH"];

  // Network (wallet internal networks: MAINNET/TRON)
  // Auto-select based on currency
  const [network, setNetwork] = useState(
    ASSET_NETWORK_MAP[currency] || defaultNetwork
  );

  // ✅ NEW: Auto-select network when currency changes
  useEffect(() => {
    const suggestedNetwork = ASSET_NETWORK_MAP[currency];
    if (suggestedNetwork) {
      setNetwork(suggestedNetwork);
    }
  }, [currency]);

  useEffect(() => setMarket(selectedMarket), [selectedMarket]);

  // --- Wallet Overview (REAL mode only really matters, but safe to load always) ---
  const { data: walletOverview, isLoading: walletLoading } = useQuery({
    queryKey: ["walletOverview"],
    queryFn: getWalletOverview,
    staleTime: 10_000,
  });

  const balances = useMemo(() => walletOverview?.balances || [], [walletOverview]);

  const availableBalance = useMemo(() => {
    // Match by asset+network first; fallback to any network for that asset.
    const asset = String(currency || "").toUpperCase();
    const net = String(network || "").toUpperCase();

    const exact = balances.find(
      (b) => String(b.asset).toUpperCase() === asset && String(b.network).toUpperCase() === net
    );
    if (exact) return Number(exact.available || 0);

    const anyNet = balances.find((b) => String(b.asset).toUpperCase() === asset);
    return Number(anyNet?.available || 0);
  }, [balances, currency, network]);

  // ✅ NEW: Check if the selected network has balance for this asset
  const hasBalanceOnSelectedNetwork = useMemo(() => {
    const asset = String(currency || "").toUpperCase();
    const net = String(network || "").toUpperCase();
    return balances.some(
      (b) => String(b.asset).toUpperCase() === asset && 
             String(b.network).toUpperCase() === net && 
             Number(b.available) > 0
    );
  }, [balances, currency, network]);

  // ✅ NEW: Find all networks where this asset has balance
  const availableNetworks = useMemo(() => {
    const asset = String(currency || "").toUpperCase();
    return balances
      .filter((b) => String(b.asset).toUpperCase() === asset && Number(b.available) > 0)
      .map((b) => b.network);
  }, [balances, currency]);

  const effectiveLeverage = Number(leverage) > 0 ? Number(leverage) : 1;
  const maxByWallet = useMemo(() => {
    if (accountMode === "DEMO") return 100000; // demo fixed amount
    return availableBalance * effectiveLeverage; // No platform cap
  }, [availableBalance, effectiveLeverage, accountMode]);

  const handleAmountChange = (value) => {
    const cleaned = sanitizeNumberInput(value);
    setAmountInput(cleaned);
    setShowMaxWarning(false); // No max warning needed
  };

  const handleMaxAmount = () => {
    setAmountInput(String(maxByWallet));
    setShowMaxWarning(false);
  };

  const delivery = useMemo(() => getOptionBySeconds(deliverySeconds), [deliverySeconds]);
  const profitPercent = delivery?.profitPercent ?? 20;
  const payoutMultiplier = useMemo(() => toPayoutMultiplier(profitPercent), [profitPercent]);

  const stakeNum = useMemo(() => {
    if (accountMode === "DEMO") return 100000;
    if (!amountInput) return 0;

    const cleaned = sanitizeNumberInput(amountInput);
    const num = parseFloat(cleaned);
    if (isNaN(num)) return 0;

    // Only cap to wallet balance, no platform cap
    return Math.min(num, maxByWallet);
  }, [amountInput, accountMode, maxByWallet]);

  const fee = useMemo(() => stakeNum * PLATFORM_FEE_RATE, [stakeNum]);
  const netStake = useMemo(() => Math.max(stakeNum - fee, 0), [stakeNum, fee]);
  const payoutIfCorrect = useMemo(() => netStake * payoutMultiplier, [netStake, payoutMultiplier]);

  const exceedsWallet = useMemo(() => {
    if (accountMode !== "REAL") return false;
    if (!amountInput) return false;
    const raw = parseFloat(sanitizeNumberInput(amountInput));
    if (!Number.isFinite(raw)) return false;
    return raw > maxByWallet + 1e-9;
  }, [amountInput, accountMode, maxByWallet]);

  const mutation = useMutation({
    mutationFn: (payload) => createTradeRequest(payload),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["tradeRequests"] });
      if (accountMode === "REAL") setAmountInput("");
      // Show success message briefly
      setTimeout(() => mutation.reset(), 5000);
    },
  });

  const canSubmit =
    accountMode === "REAL" &&
    !mutation.isPending &&
    market &&
    (direction === "UP" || direction === "DOWN") &&
    stakeNum > 0 &&
    stakeNum <= maxByWallet &&
    Number(deliverySeconds) > 0 &&
    payoutMultiplier >= 1;

  const errMsg = mutation.error?.response?.data?.detail || mutation.error?.message || "";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const payload = {
      market: String(market),
      direction,
      stakeAmount: stakeNum,
      durationSeconds: Number(deliverySeconds),
      payoutMultiplier: Number(payoutMultiplier),
      fundedAsset: currency,
      fundedNetwork: network,
    };

    await mutation.mutateAsync(payload);
  };

  // Helper to render amount with currency
  const renderAmount = (amount) => {
    if (amount <= 0) return "—";
    return `${formatAmount(amount)} ${currency}`;
  };

  // ✅ NEW: Get the recommended network label
  const getNetworkRecommendation = () => {
    const recommended = ASSET_NETWORK_MAP[currency];
    if (!recommended) return null;
    return {
      network: recommended,
      label: NETWORK_DISPLAY[recommended] || recommended,
    };
  };

  const networkRecommendation = getNetworkRecommendation();

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      {/* Market (locked) */}
      <div>
        <label className="block text-xs text-gray-400 mb-2">Market</label>
        <input
          value={market}
          readOnly
          className="w-full rounded-lg bg-gray-900/50 border border-gray-800 px-3 py-2 text-white cursor-not-allowed opacity-80"
        />
        <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
          <Info size={12} />
          Market is determined by your selection on the Trade page
        </div>
      </div>

      {/* Account mode */}
      <div>
        <label className="block text-xs text-gray-400 mb-2">Account</label>
        <div className="grid grid-cols-2 gap-2">
          <Pill active={accountMode === "REAL"} onClick={() => setAccountMode("REAL")}>
            Real
          </Pill>
          <Pill active={accountMode === "DEMO"} onClick={() => setAccountMode("DEMO")}>
            Demo
          </Pill>
        </div>

        {accountMode === "DEMO" && (
          <div className="mt-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
            Demo mode uses {formatAmount(100000)} {currency} for projection. No trades are submitted.
          </div>
        )}
      </div>

      {/* Pay With */}
      <div>
        <label className="block text-xs text-gray-400 mb-2">Pay With</label>
        <div className="flex flex-wrap gap-2">
          {supportedCurrencies.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCurrency(c)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-semibold transition ${
                currency === c
                  ? "bg-primary-600/20 border-primary-500/40 text-white"
                  : "bg-gray-900/40 border-gray-800 text-gray-300 hover:border-gray-700"
              }`}
            >
              <CurrencyIcon code={c} />
              {c}
            </button>
          ))}
        </div>

        {/* Network selector */}
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="rounded-lg bg-gray-900/40 border border-gray-800 p-3">
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-400">Network</div>
              {networkRecommendation && (
                <span className="text-[10px] text-primary-400 bg-primary-500/10 px-2 py-0.5 rounded-full">
                  Recommended
                </span>
              )}
            </div>
            <select
              value={network}
              onChange={(e) => setNetwork(e.target.value)}
              className="mt-2 w-full rounded-lg bg-gray-900/50 border border-gray-800 px-3 py-2 text-white"
            >
              <option value="MAINNET">MAINNET (Ethereum)</option>
              <option value="TRON">TRON (TRC20)</option>
            </select>
            {networkRecommendation && (
              <div className="text-[10px] text-gray-500 mt-1">
                💡 {currency} is typically on {networkRecommendation.label}
              </div>
            )}
          </div>

          <div className="rounded-lg bg-gray-900/40 border border-gray-800 p-3">
            <div className="text-xs text-gray-400">Available</div>
            <div className="mt-1 text-white font-semibold">
              {walletLoading ? "Loading..." : renderAmount(availableBalance)}
            </div>
            {!walletLoading && availableBalance === 0 && availableNetworks.length > 0 && (
              <div className="text-[10px] text-amber-300 mt-1">
                ⚠️ You have {currency} on: {availableNetworks.join(", ")}
              </div>
            )}
            {!walletLoading && availableBalance === 0 && availableNetworks.length === 0 && (
              <div className="text-[10px] text-red-300 mt-1">
                ⚠️ No {currency} balance found
              </div>
            )}
            {effectiveLeverage > 1 && (
              <div className="text-xs text-gray-500 mt-1">
                Max with leverage:{" "}
                <span className="text-gray-200 font-semibold">
                  {renderAmount(availableBalance * effectiveLeverage)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ✅ NEW: Network warning if balance exists on a different network */}
        {!walletLoading && availableBalance === 0 && availableNetworks.length > 0 && (
          <div className="mt-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
            <AlertCircle size={12} className="inline mr-1" />
            Your {currency} is on {availableNetworks.join(", ")}. 
            Please select the correct network above or deposit to the current network.
          </div>
        )}

        {/* ✅ NEW: Success indicator when balance is found on the selected network */}
        {!walletLoading && availableBalance > 0 && (
          <div className="mt-2 text-xs text-green-300 bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2">
            ✅ {formatAmount(availableBalance)} {currency} available on {network}
          </div>
        )}
      </div>

      {/* Direction */}
      <div>
        <label className="block text-xs text-gray-400 mb-2">Direction</label>
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setDirection("UP")}
            className={[
              "py-4 rounded-xl border-2 font-bold transition-all duration-200 flex items-center justify-center gap-2",
              direction === "UP"
                ? "border-green-500 bg-gradient-to-b from-green-900/20 to-green-950/30 text-green-300 shadow-[0_0_15px_rgba(34,197,94,0.2)]"
                : "border-gray-800 bg-gray-900/30 text-gray-400 hover:border-green-800 hover:text-green-200",
            ].join(" ")}
          >
            <ChevronUp size={20} className={direction === "UP" ? "text-green-300" : "text-gray-500"} />
            UP
          </button>

          <button
            type="button"
            onClick={() => setDirection("DOWN")}
            className={[
              "py-4 rounded-xl border-2 font-bold transition-all duration-200 flex items-center justify-center gap-2",
              direction === "DOWN"
                ? "border-red-500 bg-gradient-to-b from-red-900/20 to-red-950/30 text-red-300 shadow-[0_0_15px_rgba(239,68,68,0.2)]"
                : "border-gray-800 bg-gray-900/30 text-gray-400 hover:border-red-800 hover:text-red-200",
            ].join(" ")}
          >
            <ChevronDown size={20} className={direction === "DOWN" ? "text-red-300" : "text-gray-500"} />
            DOWN
          </button>
        </div>
        <div className="text-xs text-gray-500 mt-2 text-center">
          {direction === "UP" ? "Predict the price will go UP" : "Predict the price will go DOWN"}
        </div>
      </div>

      {/* Amount */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="block text-xs text-gray-400">
            Amount {accountMode === "DEMO" && <span className="text-amber-300">(Demo)</span>}
          </label>

          {accountMode === "REAL" && (
            <button
              type="button"
              onClick={handleMaxAmount}
              className="text-xs text-primary-400 hover:text-primary-300 transition-colors flex items-center gap-1"
            >
              <DollarSign size={12} />
              Max {renderAmount(maxByWallet)}
            </button>
          )}
        </div>

        <div className="relative">
          <input
            type="text"
            inputMode="decimal"
            value={accountMode === "DEMO" ? formatAmount(100000) : amountInput}
            onChange={(e) => {
              if (accountMode !== "REAL") return;
              handleAmountChange(e.target.value);
            }}
            readOnly={accountMode === "DEMO"}
            placeholder={accountMode === "DEMO" ? "100,000.00 (Demo)" : "Enter amount..."}
            className="w-full rounded-lg bg-gray-900/50 border border-gray-800 px-3 py-3 text-white placeholder-gray-600 text-lg font-medium pr-24"
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
            <div className="text-sm font-semibold text-gray-300 px-2 py-1 rounded bg-gray-900/40 border border-gray-700">
              {currency}
            </div>
          </div>
        </div>

        {exceedsWallet && (
          <div className="mt-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
            <AlertCircle size={12} className="inline mr-1" />
            Amount exceeds your available balance{effectiveLeverage > 1 ? " (with leverage limit applied)" : ""}. It will be capped at{" "}
            <span className="text-gray-200 font-semibold">
              {renderAmount(maxByWallet)}
            </span>
            .
          </div>
        )}

        {accountMode === "REAL" && (
          <div className="mt-3">
            <div className="text-xs text-gray-400 mb-2">Quick Amounts</div>
            <div className="flex flex-wrap gap-2">
              {/* ✅ Removed 100000 from quick amounts */}
              {[100, 1000, 5000, 10000, 50000, 500000, 1000000].map((amount) => (
                <button
                  key={amount}
                  type="button"
                  onClick={() => setAmountInput(String(amount))}
                  className="px-3 py-1.5 text-xs rounded-lg border bg-gray-900/40 border-gray-800 text-gray-300 hover:border-gray-700 transition"
                >
                  {formatAmount(amount)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delivery */}
      <div>
        <label className="block text-xs text-gray-400 mb-2">Delivery Time</label>
        <select
          value={deliverySeconds}
          onChange={(e) => setDeliverySeconds(Number(e.target.value))}
          className="w-full rounded-lg bg-gray-900/50 border border-gray-800 px-3 py-2 text-white"
        >
          {DELIVERY_OPTIONS.map((o) => (
            <option key={o.key} value={o.seconds}>
              {o.label} — {o.profitPercent}%
            </option>
          ))}
        </select>

        <div className="mt-2 grid grid-cols-2 gap-2">
          <div className="rounded-lg bg-gray-900/40 border border-gray-800 p-3">
            <div className="text-xs text-gray-400">Return (Auto)</div>
            <div className="text-white font-semibold">{profitPercent}%</div>
            <div className="text-xs text-gray-500 mt-1">Based on delivery time</div>
          </div>

          <div className="rounded-lg bg-gray-900/40 border border-gray-800 p-3">
            <div className="text-xs text-gray-400">Payout Multiplier</div>
            <div className="text-white font-semibold">{payoutMultiplier.toFixed(2)}x</div>
            <div className="text-xs text-gray-500 mt-1">Net stake × multiplier</div>
          </div>
        </div>
      </div>

      {/* Fees */}
      <div className="rounded-lg bg-gray-900/40 border border-gray-800 p-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-300">Platform fee</span>
          <span className="text-white font-semibold">0.5%</span>
        </div>

        <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Fee amount</span>
            <span className="text-white font-semibold">
              {stakeNum > 0 ? renderAmount(fee) : "—"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-400">Net stake</span>
            <span className="text-white font-semibold">
              {stakeNum > 0 ? renderAmount(netStake) : "—"}
            </span>
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-gray-800">
          <div className="text-xs text-gray-400">
            {accountMode === "DEMO" ? "Demo projection (if correct)" : "If prediction is correct"}
          </div>
          <div className="text-lg font-bold text-white">
            {stakeNum > 0 ? renderAmount(payoutIfCorrect) : "—"}
          </div>
          <div className="text-xs text-gray-500 mt-1">Net stake × {payoutMultiplier.toFixed(2)}x</div>
        </div>
      </div>

      {/* Trade Status Info */}
      <div className="rounded-lg bg-blue-900/20 border border-blue-800/40 p-3">
        <div className="flex items-start gap-2 text-xs text-blue-200/70">
          <Info size={14} className="flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold text-blue-200">Trade Lifecycle:</span> Your trade will be submitted. 
            It will be settled based on market conditions. 
            You can track its status in the <span className="text-blue-200 font-medium">Requests</span> page.
          </div>
        </div>
      </div>

      {/* Errors / success */}
      {errMsg && (
        <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          <AlertCircle size={14} className="inline mr-1 flex-shrink-0" />
          {errMsg}
        </div>
      )}

      {mutation.isSuccess && (
        <div className="text-sm text-green-300 bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2">
          ✅ Trade request submitted successfully! 
          <span className="block text-xs text-green-200/70 mt-1">
            You'll see it in the Requests page.
          </span>
        </div>
      )}

      {/* Submit / Demo */}
      {accountMode === "REAL" ? (
        <button
          type="submit"
          disabled={!canSubmit || walletLoading}
          className={`w-full rounded-lg bg-gradient-to-r from-primary-600 to-cyan-600 hover:from-primary-700 hover:to-cyan-700 disabled:opacity-60 transition font-semibold py-3 text-white text-lg ${
            walletLoading ? "cursor-wait" : ""
          }`}
        >
          {mutation.isPending 
            ? "Submitting…" 
            : walletLoading 
            ? "Loading balance…" 
            : stakeNum > 0 
            ? `Entrust ${renderAmount(stakeNum)}` 
            : "Enter amount to entrust"}
        </button>
      ) : (
        <div className="space-y-3">
          <div className="text-sm text-center text-gray-300 bg-gray-800/40 border border-gray-700 rounded-lg p-3">
            <div className="font-semibold text-amber-300 mb-1">Demo Mode Active</div>
            <div className="text-xs text-gray-400">
              Showing projection for {formatAmount(100000)} {currency}. Switch to <span className="text-primary-300">Real</span> to create a trade.
            </div>
          </div>
          <button
            type="button"
            className="w-full rounded-lg bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-semibold py-3 transition"
            onClick={() => setAccountMode("REAL")}
          >
            Switch to Real Mode
          </button>
        </div>
      )}

      <div className="text-xs text-gray-500 text-center">
        {accountMode === "DEMO"
          ? "Demo shows projections only. No trades are created."
          : `Maximum: ${renderAmount(maxByWallet)} (limited by your wallet balance).`}
      </div>
    </form>
  );
}
